import { Routes } from '@angular/router';
import { AuthLayoutComponent } from '@layouts/auth-layout/auth-layout.component';
import { GuestLayoutComponent } from '@layouts/guest-layout/guest-layout.component';
import { LoginComponent } from '@pages/login/login.component'; // Eager load - first page
import { LdapLoginComponent } from '@pages/ldap-login/ldap-login.component'; // Eager load - LDAP login

import { authGuard } from '@guards/auth.guard';
import { guestGuard } from './guards/guest.guard';
import { newUserGuard } from './guards/new-user.guard';
import { accessGuard } from './guards/access.guard';
import { PublicFaqsComponent } from './pages/faqs/public-faqs/public-faqs.component';
import { AboutUsComponent } from './pages/about-us/about-us.component';
import { TermsAndConditionsComponent } from './pages/terms-and-conditions/terms-and-conditions.component';
import { InvestorComponent } from './pages/users/investor/investor.component';
import { ProfileComponent } from './pages/users/profile/profile.component';
import { ConnectionRequestsComponent } from './pages/connection-requests/connection-requests.component';
import { ConnectUserComponent } from './pages/connect-user/connect-user.component';
import { NarrativeComponent } from './pages/users/narrative/narrative.component';

export const routes: Routes = [
    {
        path: '',
        component: GuestLayoutComponent,
        children: [
            {
                path: '',
                redirectTo: 'login',
                pathMatch: 'full',
                data: { title: 'Login' },
            },
            {
                path: 'login',
                component: LoginComponent, // Eager - users see immediately
                canActivate: [guestGuard],
                data: { title: 'Login' },
            },
            // {
            //     path: 'ldap-login',
            //     component: LdapLoginComponent, // Eager - LDAP login option
            //     canActivate: [guestGuard],
            //     data: { title: 'LDAP Login' },
            // },
            {
                path: 'forgot-password',
                loadComponent: () =>
                    import('@pages/forgot-password/forgot-password.component').then((m) => m.ForgotPasswordComponent), // Lazy - rarely used
                canActivate: [guestGuard],
                data: { title: 'Forgot Password' },
            },
            {
                path: 'reset-password',
                redirectTo: 'login',
                pathMatch: 'full',
            },
            {
                path: 'faqs',
                canActivate: [guestGuard],
                component: PublicFaqsComponent,
                data: { title: 'FAQs' },
            },
            {
                path: 'about-us',
                canActivate: [guestGuard],
                component: AboutUsComponent,
                data: { title: 'About Us' },
            },
            {
                path: 'terms-and-conditions',
                canActivate: [guestGuard],
                component: TermsAndConditionsComponent,
                data: { title: 'Terms and Conditions' },
            },
            {
                path: 'reset-password/:token',
                loadComponent: () =>
                    import('@pages/reset-password/reset-password.component').then((m) => m.ResetPasswordComponent), // Lazy - rarely used
                canActivate: [guestGuard],
                data: { title: 'Reset Password' },
            },
            {
                path: 'new-user-password',
                loadComponent: () =>
                    import('@app/pages/new-password/new-password.component').then((m) => m.NewPasswordComponent), // Lazy - rarely used
                canActivate: [newUserGuard],
                data: { title: 'New User Password' },
            },
            {
                path: 'register',
                loadChildren: () =>
                    import('@pages/registration/registration.routes').then((m) => m.REGISTRATION_ROUTES),
            },
            {
                path: 'survey',
                loadChildren: () => import('./pages/survey/survey.routes').then((m) => m.SURVEY_ROUTES),
            },
        ],
    },
    {
        path: '',
        component: AuthLayoutComponent,
        canActivate: [authGuard],
        children: [
            {
                path: '',
                redirectTo: 'home',
                pathMatch: 'full',
            },
            {
                path: 'home',
                loadComponent: () => import('@pages/home/home.component').then((m) => m.HomeComponent),
                data: { title: 'Home' },
            },
            {
                path: 'account-suspended',
                loadComponent: () =>
                    import('@pages/account-suspended/account-suspended.component').then(
                        (m) => m.AccountSuspendedComponent
                    ),
                data: { title: 'Account Suspended' },
            },
            {
                path: 'chat',
                loadComponent: () => import('@pages/chat/chat.component').then((m) => m.ChatComponent),
                canActivate: [accessGuard],
                data: { title: 'Chat', roles: 'Registrant' },
            },
            // {
            //     path: 'dashboard',
            //     canActivate: [accessGuard],
            //     loadComponent: () => import('@pages/dashboard/dashboard.component').then((m) => m.DashboardComponent), // Lazy Loaded - heavy
            //     data: { title: 'Dashboard', permission: 'view dashboard' },
            // },
            {
                path: 'application-registration',
                loadChildren: () =>
                    import('@app/pages/application/application.routes').then((m) => m.APPLICATION_ROUTES),
            },
            // {
            //     path: 'account-update',
            //     loadChildren: () =>
            //         import('@app/pages/account-update/account-update.route').then((m) => m.ACCOUNT_UPDATE_ROUTES),
            // },
            // {
            //     path: 'manage-faqs',
            //     canActivate: [accessGuard],
            //     loadComponent: () =>
            //         import('@pages/faqs/manage-faqs/manage-faqs.component').then((m) => m.ManageFaqsComponent),
            //     data: { title: 'Manage FAQs', permission: 'view manage-master-data' },
            // },
            // {
            //     path: 'manage-faq-categories',
            //     canActivate: [accessGuard],
            //     loadComponent: () =>
            //         import('@pages/faq-categories/faq-categories.component').then((m) => m.FaqCategoriesComponent),
            //     data: { title: 'Manage FAQ Categories', permission: 'view manage-master-data' },
            // },
            // {
            //     path: 'internal-users',
            //     loadChildren: () =>
            //         import('@app/pages/users/internal/internal-user.routes').then((m) => m.INTERNAL_USERS_ROUTES),
            // },
            // {
            //     path: 'external-users',
            //     loadChildren: () =>
            //         import('@app/pages/users/external/external-user.routes').then((m) => m.EXTERNAL_USERS_ROUTES),
            // },
            // {
            //     path: 'internal-role-management',
            //     loadChildren: () =>
            //         import('@app/pages/roles/internal/internal-role.routes').then((m) => m.INTERNAL_ROLE_ROUTES),
            // },
            {
                path: 'investor-investee-connect',
                canActivate: [accessGuard],
                component: InvestorComponent,
                data: { title: 'Investor/Investee Connect', roles: 'Registrant' },
            },
            {
                path: 'connection-requests',
                canActivate: [accessGuard],
                component: ConnectionRequestsComponent,
                data: { title: 'Investor/Investee Connection Requests', roles: 'Registrant' },
            },
            {
                path: 'connect-with-users',
                canActivate: [accessGuard],
                component: ConnectUserComponent,
                data: { title: 'Investor/Investee Connect', roles: 'Registrant' },
            },
            {
                path: 'connect-with-users/feedback',
                canActivate: [accessGuard],
                component: ConnectUserComponent,
                data: { title: 'Investor/Investee Connect', roles: 'Registrant', openFeedback: true },
            },
            {
                path: 'profile-narrative',
                canActivate: [accessGuard],
                component: NarrativeComponent,
                data: { title: 'Investor/Investee Profile', roles: 'Registrant' },
            },
            {
                path: 'investor-investee-profile',
                canActivate: [accessGuard],
                component: ProfileComponent,
                data: { title: 'Investor/Investee Profile', roles: 'Registrant' },
            },
            {
                path: 'activity-logs',
                loadComponent: () =>
                    import('@pages/activity-logs/activity-logs.component').then((m) => m.ActivityLogsComponent),
                canActivate: [accessGuard],
                data: { title: 'Activity Logs', roles: 'Registrant' },
            },
            {
                path: 'help/faqs',
                component: PublicFaqsComponent,
                data: { title: 'FAQs' },
            },
            {
                path: 'help/about-us',
                component: AboutUsComponent,
                data: { title: 'About Us' },
            },
            {
                path: 'help/terms-and-conditions',
                component: TermsAndConditionsComponent,
                data: { title: 'Terms and Conditions' },
            },
            {
                path: 'change-password',
                loadComponent: () =>
                    import('@pages/change-password/change-password.component').then((m) => m.ChangePasswordComponent),
                data: { title: 'Change Password' },
            },
            // {
            //     path: 'system-configuration',
            //     loadChildren: () =>
            //         import('@app/pages/system-configuration/system-configuration.routes').then(
            //             (m) => m.SYSTEM_CONFIGURATION_ROUTES
            //         ),
            // },
            // {
            //     path: 'documentary-requirements',
            //     loadChildren: () =>
            //         import('@app/pages/documentary-requirements/documentary-requirements.routes').then(
            //             (m) => m.DOCUMENTARY_REQUIREMENTS_ROUTES
            //         ),
            // },
            // {
            //     path: 'approval-status',
            //     loadChildren: () =>
            //         import('@app/pages/process-status/process-status.routes').then((m) => m.PROCESS_STATUS_ROUTES),
            // },
            // {
            //     path: 'banners',
            //     loadChildren: () => import('@app/pages/banners/banners.routes').then((m) => m.BANNERS_ROUTES),
            // },
            // {
            //     path: 'prohibited-words',
            //     loadComponent: () =>
            //         import('@pages/prohibited-words/prohibited-words-list/prohibited-words-list.component').then(
            //             (m) => m.ProhibitedWordsListComponent
            //         ),
            //     canActivate: [accessGuard],
            //     data: { title: 'Prohibited Words', permission: 'view manage-master-data' },
            // },
            // {
            //     path: 'approvals/de-listing',
            //     loadChildren: () => import('./pages/de-listing/de-listing.routes').then((m) => m.DE_LISTING_ROUTES),
            // },
            // {
            //     path: 'approvals/involuntary-de-listing',
            //     loadChildren: () =>
            //         import('./pages/involuntary-de-listing/involuntary-de-listing.routes').then(
            //             (m) => m.INVOLUNTARY_DE_LISTING_ROUTES
            //         ),
            // },
            // {
            //     path: 'alerts',
            //     loadChildren: () => import('@app/pages/alerts/alerts.routes').then((m) => m.ALERTS_ROUTES),
            // },
            // {
            //     path: 'login-logs',
            //     loadComponent: () => import('@pages/login-logs/login-logs.component').then((m) => m.LoginLogsComponent),
            //     canActivate: [accessGuard],
            //     data: { title: 'Login Logs', permission: 'view login-logs' },
            // },
            // {
            //     path: 'logs-analysis-and-reports',
            //     loadComponent: () =>
            //         import('@pages/logs-analysis-and-reports/logs-analysis-and-reports.component').then(
            //             (m) => m.LogsAnalysisAndReportsComponent
            //         ),
            //     canActivate: [accessGuard],
            //     data: { title: 'Logs Analysis and Reports', permission: 'view audit-trail' },
            // },
        ],
    },
    // Error pages
    {
        path: 'forbidden',
        loadComponent: () => import('@pages/forbidden/forbidden.component').then((m) => m.ForbiddenComponent),
        data: { title: 'Access Forbidden' },
    },
    {
        path: 'not-found',
        loadComponent: () => import('@pages/not-found/not-found.component').then((m) => m.NotFoundComponent),
        data: { title: 'Page Not Found' },
    },
    // Wildcard route
    {
        path: '**',
        redirectTo: '/not-found',
    },
];
